import type { ReactNode } from 'react';
import { Clock, Shield } from 'lucide-react';

export type AuditEntity = {
  criadoEm?: string;
  atualizadoEm?: string;
  ativo?: boolean;
};

function fmtAudit(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

export function ConfigEntityFichaFooter({
  entity,
  canManage,
  onEdit,
  onDeactivate,
  onReactivate,
  deactivateLabel = 'Desativar',
  reactivateLabel = 'Reativar',
  beforeMeta,
  beforeActions,
  afterEdit,
  statusAfterMeta = false,
}: {
  entity: AuditEntity;
  canManage: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate?: () => void;
  deactivateLabel?: string;
  reactivateLabel?: string;
  /** Controles à esquerda (ex.: Redefinir Turno na ficha de turma). */
  beforeMeta?: ReactNode;
  /** Controles antes de Editar (legado). */
  beforeActions?: ReactNode;
  /** Controles logo após Editar (ex.: Salvar na ficha de turma). */
  afterEdit?: ReactNode;
  /** Desativar/Reativar logo após a data, à esquerda, sem borda. */
  statusAfterMeta?: boolean;
}) {
  const ativo = entity.ativo !== false;
  const putStatusLeft = statusAfterMeta || !!afterEdit;
  const statusAction =
    canManage &&
    (ativo ? (
      <button
        type="button"
        className={`resp-ficha-btn ${putStatusLeft ? 'resp-ficha-btn--danger-plain' : 'resp-ficha-btn--danger'}`}
        onClick={onDeactivate}
      >
        {deactivateLabel}
      </button>
    ) : (
      onReactivate && (
        <button
          type="button"
          className={`resp-ficha-btn ${putStatusLeft ? 'resp-ficha-btn--success-plain' : 'resp-ficha-btn--success'}`}
          onClick={onReactivate}
        >
          {reactivateLabel}
        </button>
      )
    ));

  return (
    <div className="resp-ficha-foot">
      {beforeMeta}
      <span className="resp-ficha-foot-meta">
        {entity.criadoEm
          ? `Cadastrado em ${new Date(entity.criadoEm).toLocaleDateString('pt-BR')}`
          : '—'}
      </span>
      {putStatusLeft ? statusAction : null}
      <span className="resp-ficha-foot-spacer" />
      <div className="resp-ficha-foot-actions">
        {beforeActions}
        {canManage && (
          <button
            type="button"
            className={`resp-ficha-btn${afterEdit ? '' : ' resp-ficha-btn--primary'}`}
            onClick={onEdit}
          >
            Editar
          </button>
        )}
        {afterEdit}
        {!putStatusLeft ? statusAction : null}
      </div>
    </div>
  );
}

export function ConfigEntitySidePanel({
  entity,
  kpis,
  hints,
}: {
  entity?: AuditEntity | null;
  kpis: { icon?: ReactNode; label: string; value: ReactNode }[];
  hints?: string[];
}) {
  return (
    <div className="ac-modal-dual__side ac-equipe-edit-side">
      <div className="ac-modal-dual__side-panel">
        <div className="ac-modal-dual__side-head">
          <Shield size={14} strokeWidth={1.5} />
          <strong>Contexto</strong>
        </div>
        <div className="ac-modal-dual__side-body">
          <div className="ac-equipe-edit-kpis">
            {kpis.map((k) => (
              <div key={k.label} className="ac-equipe-edit-kpi">
                {k.icon ?? <Clock size={12} />}
                <span>
                  {k.label}: <strong>{k.value}</strong>
                </span>
              </div>
            ))}
          </div>
          {hints?.map((h) => (
            <p key={h} className="ac-equipe-edit-hint">
              {h}
            </p>
          ))}
          <dl className="ac-equipe-edit-dl">
            <div>
              <dt>Inclusão</dt>
              <dd>{fmtAudit(entity?.criadoEm)}</dd>
            </div>
            <div>
              <dt>Alteração</dt>
              <dd>{fmtAudit(entity?.atualizadoEm)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
